<template>
  <div class="positions-pay-ranges-page">
    <EXPermissionDeniedState v-if="state.forbidden" />
    <PositionsWithPayRangeSubscriptionsTable
      v-else
      :positions="state.rows"
      :as-of-date="state.asOfDate"
      :as-of-state="state.asOfState"
      :filter-chips="filterChips"
      :page="state.page"
      :page-count="pageCount"
      :loading="state.loading"
      :error="state.error"
      @retry="fetchRows"
      @update:search="onUpdateSearch"
      @update:page="onUpdatePage"
      @update:asOfDate="onUpdateAsOfDate"
      @reset-to-today="onResetToToday"
      @remove-filter="onRemoveFilter"
      @open-more-filters="onOpenMoreFilters"
      @subscribe-position="onSubscribePosition"
      @unsubscribe-position="onUnsubscribePosition"
      @open-position="onOpenPosition"
      @open-position-chatter="onOpenPositionChatter"
      @navigate-to-previous-version="onNavigateToVersion"
      @navigate-to-next-version="onNavigateToVersion"
      :version-navigation-enabled="versionNavigationEnabled"
      @navigate-to-previous-subscription="onNavigateToSubscription"
      @navigate-to-next-subscription="onNavigateToSubscription"
      :subscription-navigation-enabled="subscriptionNavigationEnabled"
      :coverage-gap-enabled="coverageGapEnabled"
    />

    <SearchModal
      :dialog="state.filters.showFilterDialog"
      filterType="Positions"
      :status-options="STATUS_OPTIONS"
      :filter-groups="FILTER_GROUPS"
      :default-selected="filterModalDefaultSelected"
      @back="closeFilterModal"
      @checkedItemsChanged="onFiltersApplied"
    />

    <SubscribeToPayRangeDialog
      v-if="state.subscribeDialog.show"
      :position="state.subscribeDialog.position"
      :pay-range-id="state.subscribeDialog.payRangeId"
      :pay-range-options="state.subscribeDialog.payRangeOptions"
      :pay-range-search-loading="state.subscribeDialog.payRangeSearchLoading"
      :selected-pay-range-preview="state.subscribeDialog.selectedPayRangePreview"
      :effective-date="state.subscribeDialog.effectiveDate"
      :existing-subscription="state.subscribeDialog.existingSubscription"
      :published-version-ranges="state.subscribeDialog.publishedVersionRanges"
      :submitting="state.subscribeDialog.submitting"
      @update:payRangeId="onPayRangeChange"
      @search="onPayRangeSearch"
      @update:effectiveDate="onUpdateEffectiveDate"
      @subscribe="onSubscribeSubmit"
      @cancel="onSubscribeCancel"
    />

    <UnsubscribeFromPayRangeDialog
      v-if="state.unsubscribeDialog.show"
      :pay-range-name="state.unsubscribeDialog.payRangeName"
      :pay-range-code="state.unsubscribeDialog.payRangeCode"
      :effective-from="state.unsubscribeDialog.effectiveFrom"
      :subscription-effective-to="state.unsubscribeDialog.subscriptionEffectiveTo"
      :effective-to="state.unsubscribeDialog.effectiveTo"
      :submitting="state.unsubscribeDialog.submitting"
      @update:effectiveTo="onUpdateUnsubscribeEffectiveTo"
      @unsubscribe="onUnsubscribeSubmit"
      @cancel="onUnsubscribeCancel"
    />

    <ChatterDrawer
      :drawer="state.chatterDrawer.show"
      type="Position"
      :type-value="state.chatterDrawer.position?.name || ''"
      object-type="position"
      :object-id="state.chatterDrawer.position?.id ?? null"
      icon-name="org_icon_position"
      :chatter-list="state.chatterDrawer.chatterData"
      :total-checkpoint-chatter="state.chatterDrawer.total"
      @closeDrawer="onCloseChatterDrawer"
      @requestMoreCheckpointsChatter="onRequestMoreChatter"
    />
  </div>
</template>

<script setup>
import { ref, getCurrentInstance, onMounted } from "vue";
import PositionsWithPayRangeSubscriptionsTable, {
  AS_OF_STATE,
} from "@components/PositionsWithPayRangeSubscriptionsTable/PositionsWithPayRangeSubscriptionsTable.vue";
import SubscribeToPayRangeDialog from "@components/SubscribeToPayRangeDialog/SubscribeToPayRangeDialog.vue";
import UnsubscribeFromPayRangeDialog from "@components/UnsubscribeFromPayRangeDialog/UnsubscribeFromPayRangeDialog.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import EXPermissionDeniedState from "@components/EXPermissionDeniedState/EXPermissionDeniedState.vue";
import {
  LD_FLAG_FAIR_PAY_VERSION_NAVIGATION,
  LD_FLAG_FAIR_PAY_SUBSCRIPTION_NAVIGATION,
  LD_FLAG_IDEA_MDP_429,
} from "@components/constants/launchDarklyFlagKeys";
import launchDarklyService from "@/services/launchdarkly";
import {
  usePositionsPayRangesPageState,
  STATUS_OPTIONS,
  FILTER_GROUPS,
} from "./composables/usePositionsPayRangesPageState";

const { proxy } = getCurrentInstance() || {};

const {
  state,
  pageCount,
  filterChips,
  filterModalDefaultSelected,
  fetchRows,
  onUpdateSearch,
  onUpdatePage,
  onUpdateAsOfDate,
  onResetToToday,
  onOpenMoreFilters,
  closeFilterModal,
  onFiltersApplied,
  onRemoveFilter,
  onOpenPositionChatter,
  onCloseChatterDrawer,
  onRequestMoreChatter,
  onSubscribePosition,
  onPayRangeSearch,
  onPayRangeChange,
  onUpdateEffectiveDate,
  onSubscribeSubmit,
  onSubscribeCancel,
  onUnsubscribePosition,
  onUpdateUnsubscribeEffectiveTo,
  onUnsubscribeSubmit,
  onUnsubscribeCancel,
} = usePositionsPayRangesPageState({
  // Restore the list's as-of date when returning from a Position Fair Pay card
  // (the breadcrumb carries it in `?asOf=`).
  initialAsOfDate: proxy?.$route?.query?.asOf || null,
});

/** Rule 5 — version-navigation chevrons are flag-gated; off by default. */
const versionNavigationEnabled = ref(false);

/** Rule 4 — subscription-navigation chevrons are flag-gated; off by default. */
const subscriptionNavigationEnabled = ref(false);

/** ER-11714 — coverage-gap pill is flag-gated (idea-MDP-429); off by default. */
const coverageGapEnabled = ref(false);

onMounted(async () => {
  fetchRows();
  try {
    const [versionEnabled, subscriptionEnabled, coverageEnabled] = await Promise.all([
      launchDarklyService.getFlag(LD_FLAG_FAIR_PAY_VERSION_NAVIGATION, false),
      launchDarklyService.getFlag(LD_FLAG_FAIR_PAY_SUBSCRIPTION_NAVIGATION, false),
      launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_429, false),
    ]);
    versionNavigationEnabled.value = versionEnabled === true;
    subscriptionNavigationEnabled.value = subscriptionEnabled === true;
    coverageGapEnabled.value = coverageEnabled === true;
  } catch {
    versionNavigationEnabled.value = false;
    subscriptionNavigationEnabled.value = false;
    coverageGapEnabled.value = false;
  }
});

/** Rule 5 — the consuming page applies the card's clamped targetDate to asOfDate. */
function onNavigateToVersion(payload) {
  if (payload?.targetDate) {
    onUpdateAsOfDate(payload.targetDate);
  }
}

/** Rule 4 — the consuming page applies the subscription-chevron's targetDate to asOfDate. */
function onNavigateToSubscription(payload) {
  if (payload?.targetDate) {
    onUpdateAsOfDate(payload.targetDate);
  }
}

function onOpenPosition(row) {
  if (!row?.id || !proxy?.$router) return;
  const route = { name: "FairPayPosition", params: { positionId: String(row.id) } };
  // Remember a custom as-of date so the breadcrumb back can restore the row's
  // subscription (including future-dated subscriptions) instead of
  // "+ Subscribe to Pay Range".
  if (state.asOfState === AS_OF_STATE.CUSTOM_DATE) {
    route.query = { asOf: state.asOfDate };
  }
  proxy.$router.push(route).catch(() => {});
}
</script>

<style scoped>
.positions-pay-ranges-page {
  width: 100%;
  padding: 8px 0 24px;
}
</style>
